import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBanners } from '../context/BannerContext';
import { useUserProfile } from '../context/UserProfileContext';
import { createElement } from 'react';
import { CreditCard } from 'lucide-react';

const BANNER_ID = 'subscription-required';

/**
 * Registers (or removes) the "Subscription Required" banner in BannerContext
 * based on the user's org subscription status.
 *
 * Also exposes `noSubscription` so pages can disable cards / hide FABs.
 */
export function useSubscriptionBanner() {
  const { hasActiveSubscription, activeOrg } = useUserProfile();
  const { addBanner, removeBanner } = useBanners();
  const navigate = useNavigate();

  const noSubscription = !hasActiveSubscription;
  const isAdmin = activeOrg?.role === 'admin';

  useEffect(() => {
    if (!noSubscription) {
      removeBanner(BANNER_ID);
      return;
    }

    addBanner({
      id: BANNER_ID,
      priority: 10,
      content: createElement(SubscriptionBanner, {
        isAdmin,
        onSetup: () => navigate('/admin/company/billing/plan'),
      }),
    });

    return () => removeBanner(BANNER_ID);
  }, [noSubscription, isAdmin, addBanner, removeBanner, navigate]);

  return { noSubscription };
}

/** Compact single-row subscription banner. */
function SubscriptionBanner({ isAdmin, onSetup }: { isAdmin: boolean; onSetup: () => void }) {
  return createElement(
    'div',
    {
      className:
        'bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-3',
    },
    // Left: icon + label
    createElement(
      'div',
      { className: 'flex items-center gap-2 min-w-0' },
      createElement(CreditCard, { size: 16, className: 'shrink-0 text-amber-600' }),
      createElement(
        'p',
        { className: 'text-small font-semibold text-amber-800 truncate' },
        'Subscription Required',
      ),
    ),
    // Right: CTA
    isAdmin
      ? createElement(
          'button',
          {
            type: 'button',
            onClick: onSetup,
            className:
              'shrink-0 text-small font-semibold text-plum hover:text-rose transition-colors whitespace-nowrap',
          },
          'Set Up Subscription',
        )
      : createElement(
          'span',
          { className: 'shrink-0 text-small text-slate whitespace-nowrap' },
          'Contact your Admin',
        ),
  );
}
